import React, { useEffect, useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import SchoolIcon from '@mui/icons-material/School';
import GavelIcon from '@mui/icons-material/Gavel';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import CampaignIcon from '@mui/icons-material/Campaign';
import CodeIcon from '@mui/icons-material/Code';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';
import EngineeringIcon from '@mui/icons-material/Engineering';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import PlaceIcon from '@mui/icons-material/Place';
import GrassIcon from '@mui/icons-material/Grass';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import FlightIcon from '@mui/icons-material/Flight';
import PinDropIcon from '@mui/icons-material/PinDrop';
import StoreIcon from '@mui/icons-material/Store';
import WorkIcon from '@mui/icons-material/Work';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PsychologyIcon from '@mui/icons-material/Psychology';
import BuildIcon from '@mui/icons-material/Build';
import StarIcon from '@mui/icons-material/Star';
import AppsIcon from '@mui/icons-material/Apps';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import { useAuth0 } from '@auth0/auth0-react';
import { displayGroups, promptCategories, getPromptGroup } from './promptsData';
import { API_BASE } from '../../shared/Constants';
import { useFeatureFlags } from '../../shared/context/FeatureFlagsContext';

// Icon mapping
const iconMap: Record<string, React.ElementType> = {
  School: SchoolIcon,
  Gavel: GavelIcon,
  LocalHospital: LocalHospitalIcon,
  ChildCare: ChildCareIcon,
  Campaign: CampaignIcon,
  Code: CodeIcon,
  BusinessCenter: BusinessCenterIcon,
  Engineering: EngineeringIcon,
  FavoriteBorder: FavoriteBorderIcon,
  Place: PlaceIcon,
  Grass: GrassIcon,
  Analytics: AnalyticsIcon,
  Flight: FlightIcon,
  PinDrop: PinDropIcon,
  Store: StoreIcon,
  Work: WorkIcon,
  AccountBalance: AccountBalanceIcon,
  Psychology: PsychologyIcon,
  Build: BuildIcon,
  Star: StarIcon,
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 20px;
  width: 100%;
  max-width: 700px;
`;

const IntroText = styled.span`
  color: ${({ theme }) => theme.text_secondary};
  font-size: 0.82rem;
  margin-bottom: 10px;
`;

const CardsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  width: 100%;

  @media (max-width: 600px) {
    & > button:nth-child(n+5) {
      display: none;
    }
  }
`;

const CategoryCard = styled.button<{ $accentColor?: string }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.borderGrey};
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${({ theme }) => theme.text_secondary};
  font-size: 0.82rem;
  font-family: 'Sora', 'Lexend', sans-serif;

  &:hover {
    border-color: ${({ $accentColor, theme }) => $accentColor || theme.prompt_accent_color};
    color: ${({ theme }) => theme.font_color};
    background: ${({ theme }) => theme.prompt_item_hover};
  }

  svg {
    font-size: 1rem;
    color: ${({ theme }) => theme.text_secondary};
    transition: color 0.2s ease;
  }

  &:hover svg {
    color: ${({ $accentColor, theme }) => $accentColor || theme.prompt_accent_color};
  }
`;

const PromptCount = styled.span<{ $accentColor?: string }>`
  font-size: 0.7rem;
  color: ${({ theme }) => theme.prompt_accent_color};
  background: ${({ theme }) => theme.prompt_category_icon_bg};
  padding: 1px 7px;
  border-radius: 10px;
  font-weight: 500;
`;

// Models that have access to Data Ocean tools (BigQuery, APIs, etc.)
const DATA_OCEAN_MODELS = new Set(["sabia-4", "sabia-4-thinking", "sabiazinho-4", "competidor", "competidor-2", "competidor-3"]);

// Original categories from main branch (shown when Data Ocean is disabled)
const MAIN_BRANCH_CATEGORIES = new Set(['education', 'legal', 'health', 'wellness', 'marketing', 'tech', 'business', 'engineering']);

interface CategoryCardsProps {
  onSelectCategory: (categoryId: string) => void;
  refreshTrigger?: number;
  modelColor?: string;
  modelAccent?: string;
  modelName?: string;
}

const CategoryCards: React.FC<CategoryCardsProps> = ({ onSelectCategory, refreshTrigger, modelColor, modelAccent, modelName }) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { data_ocean_tools } = useFeatureFlags();
  const [customPromptsCount, setCustomPromptsCount] = useState(0);

  const hasToolAccess = data_ocean_tools && DATA_OCEAN_MODELS.has(modelName || '');

  // Non-DO models (sabia-4) show original main-branch categories, same as when flag is off
  const useSimpleCategories = !hasToolAccess;

  // All display groups for DO models
  const visibleGroups = useMemo(() => {
    if (useSimpleCategories) return [];
    return displayGroups;
  }, [useSimpleCategories]);

  // Simple categories (main-branch style)
  const simpleCategories = useMemo(() => {
    if (!useSimpleCategories) return [];
    return promptCategories.filter(c => MAIN_BRANCH_CATEGORIES.has(c.id));
  }, [useSimpleCategories]);

  const loadCustomPromptsCount = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE}/api/prompts/list`, {
        method: 'GET',
        headers: {
          Authorization: `Auth ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCustomPromptsCount(data.prompts?.length || 0);
      }
    } catch (error) {
      console.error('Failed to load custom prompts count:', error);
    }
  }, [getAccessTokenSilently, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      loadCustomPromptsCount();
    }
  }, [isAuthenticated, loadCustomPromptsCount, refreshTrigger]);

  return (
    <Wrapper>
      <IntroText>{useSimpleCategories ? 'Comece com a biblioteca de prompts para as áreas:' : 'Explore por tema ou comece com os destaques:'}</IntroText>
      <CardsContainer>
        {/* My Prompts Card */}
        {isAuthenticated && (
          <CategoryCard
            onClick={() => onSelectCategory('my-prompts')}
            type="button"
            $accentColor={modelColor}
          >
            <FolderSpecialIcon />
            Meus Prompts
            {customPromptsCount > 0 && (
              <PromptCount $accentColor={modelColor}>{customPromptsCount}</PromptCount>
            )}
          </CategoryCard>
        )}
        {useSimpleCategories
          ? simpleCategories.map((category) => {
              const IconComponent = iconMap[category.icon] || SchoolIcon;
              return (
                <CategoryCard
                  key={category.id}
                  onClick={() => onSelectCategory(category.id)}
                  type="button"
                  $accentColor={modelColor}
                >
                  <IconComponent />
                  {category.name}
                </CategoryCard>
              );
            })
          : <>
              {visibleGroups.map((group) => {
                const IconComponent = iconMap[group.icon] || SchoolIcon;
                return (
                  <CategoryCard
                    key={group.id}
                    onClick={() => onSelectCategory(group.id)}
                    type="button"
                    $accentColor={modelColor}
                  >
                    {group.id === 'featured' ? <StarIcon /> : <IconComponent />}
                    {group.name}
                  </CategoryCard>
                );
              })}
              <CategoryCard
                key="all"
                onClick={() => onSelectCategory('all')}
                type="button"
                $accentColor={modelColor}
              >
                <AppsIcon />
                Todos
              </CategoryCard>
            </>}
      </CardsContainer>
    </Wrapper>
  );
};

export default CategoryCards;
